import { useCallback } from "react";
import { readAuthoredItem } from "../../api/content/authoring/processAuthoringApi";
import { useLoad } from "../../shared/loading/useLoad";
import { createAnnotationLabelOptions, type AnnotationLabelOption } from "./annotationLabels";

export interface LabelListNames {
  readonly listName: string;
  /** The readable name of each label key. */
  readonly labels: ReadonlyMap<string, string>;
  /** The labels a span may take, in the list's order. */
  readonly options: AnnotationLabelOption[];
}

/**
 * The names of an annotation set's label list (`<item id>@<version>`; `null` for no set). `null`
 * while they load, and when the list can't be read (no access, or gone): labels then show by
 * their keys.
 */
export function useLabelListNames(labelList: string | null): LabelListNames | null {
  const load = useCallback(async (signal: AbortSignal): Promise<LabelListNames | null> => {
    if (labelList === null) {
      return null;
    }
    const item = await readAuthoredItem(labelList, signal).catch(() => null);
    if (item === null) {
      return null;
    }
    const labels = item.content?.kind === "label_list" ? item.content.definition.labels : [];
    return {
      listName: item.name,
      labels: new Map(labels.map((label) => [label.key, label.name])),
      options: createAnnotationLabelOptions(labels.map((label) => label.key)),
    };
  }, [labelList]);
  const { state } = useLoad(load);
  return state.status === "loaded" ? state.value : null;
}
